import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Tag, message, Space, Button, Modal, Form, Input, InputNumber, DatePicker, Select } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';
import dayjs from 'dayjs';

const { Title, Paragraph } = Typography;

const DraftsScheduled = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const { token } = useAuth();
  const [pubOpen, setPubOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [form] = Form.useForm();

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Type', dataIndex: 'type', key: 'type', render: t => <Tag color={t === 'draft' ? 'default' : 'blue'}>{t}</Tag>, width: 120 },
    { title: 'Scheduled Start', dataIndex: 'scheduled', key: 'scheduled', width: 180 },
    { title: 'Budget', dataIndex: 'budget', key: 'budget', render: v => `$${v.toLocaleString?.() || v}`, width: 120 },
    {
      title: 'Actions', key: 'actions', width: 260,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openPublish(record, 'facebook')}>Publish FB</Button>
          <Button size="small" onClick={() => openPublish(record, 'google')}>Publish Google</Button>
          <Button size="small" type="primary" onClick={() => openPublish(record, 'both')}>Publish Both</Button>
        </Space>
      )
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (!token) return;
        const res = await fetch('/api/campaigns', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load campaigns');
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        const now = new Date();
        const mapped = items
          .map((c) => {
            const start = c.start_date ? new Date(c.start_date) : null;
            const isDraft = String(c.status || '').toLowerCase() === 'draft';
            const isScheduled = !!start && start > now && !isDraft;
            if (!isDraft && !isScheduled) return null;
            return {
              id: c.id,
              name: c.name,
              type: isDraft ? 'draft' : 'scheduled',
              scheduled: start ? start.toISOString().replace('T', ' ').slice(0, 16) : '-',
              budget: c.budget,
              raw: c,
            };
          })
          .filter(Boolean);
        setRows(mapped);
      } catch (e) {
        message.error(e.message || 'Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const openPublish = (record, provider = 'both') => {
    // seed modal with campaign values
    const start = record?.raw?.start_date ? dayjs(record.raw.start_date) : null;
    const end = record?.raw?.end_date ? dayjs(record.raw.end_date) : null;
    form.setFieldsValue({
      name: record?.name,
      provider,
      daily_budget: record?.raw?.budget != null ? Number(record.raw.budget) : undefined,
      start_date: start,
      end_date: end,
      radius_km: 5,
      address: '',
    });
    setPubOpen(true);
  };

  const publishNow = async () => {
    try {
      const v = await form.validateFields();
      setPublishing(true);
      const payload = {
        name: v.name,
        daily_budget: v.daily_budget != null ? Number(v.daily_budget) : undefined,
        total_budget: undefined,
        schedule: {
          start_date: v.start_date ? v.start_date.toISOString() : undefined,
          end_date: v.end_date ? v.end_date.toISOString() : undefined,
        },
        targeting: {
          address: v.address,
          radius_km: v.radius_km != null ? Number(v.radius_km) : 5,
        },
      };
      const doFacebook = v.provider === 'facebook' || v.provider === 'both';
      const doGoogle = v.provider === 'google' || v.provider === 'both';
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

      const calls = [];
      if (doFacebook) {
        calls.push(fetch('/api/campaigns/facebook', { method: 'POST', headers, body: JSON.stringify(payload) }));
      }
      if (doGoogle) {
        calls.push(fetch('/api/campaigns/google', { method: 'POST', headers, body: JSON.stringify(payload) }));
      }
      const results = await Promise.all(calls);
      for (const res of results) {
        if (!res.ok) {
          let err = 'Publish failed';
          try { const j = await res.json(); if (j?.error) err = j.error; if (j?.errors) err = j.errors.join(', '); } catch {}
          throw new Error(err);
        }
      }
      message.success('Publish queued');
      setPubOpen(false);
    } catch (e) {
      if (e?.errorFields) return; // validation
      message.error(e.message || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Campaigns' }, { title: 'Drafts & Scheduled' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Drafts & Scheduled</Title>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Manage drafts and upcoming scheduled campaigns.</Paragraph>
        <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 10 }} />
      </Card>
      <Modal
        open={pubOpen}
        title="Publish Campaign"
        okText="Publish"
        confirmLoading={publishing}
        onOk={publishNow}
        onCancel={() => { setPubOpen(false); }}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Provider" name="provider" rules={[{ required: true }]}>
            <Select options={[
              { value: 'facebook', label: 'Facebook' },
              { value: 'google', label: 'Google' },
              { value: 'both', label: 'Both' },
            ]} />
          </Form.Item>
          <Form.Item label="Daily Budget (USD)" name="daily_budget" rules={[{ required: true }] }>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Start Date" name="start_date" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="End Date" name="end_date">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Target Address" name="address" tooltip="Used to geocode the location" rules={[{ required: true, message: 'Address is required' }]}>
            <Input placeholder="e.g., 1600 Amphitheatre Pkwy, Mountain View" />
          </Form.Item>
          <Form.Item label="Radius (km)" name="radius_km" initialValue={5}>
            <InputNumber min={1} max={50} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DraftsScheduled;
